import React, { Suspense, lazy, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import "./assets/css/style.css";
import "./assets/css/ResponsiveModals.css";


import { ConfigProvider, notification, Select } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";

import { store, persistor } from "./redux/store"; // Adjust the path as necessary

import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

const Login = lazy(() => import("./pages/auth/login"));
const ForgotPassword = lazy(() => import("./pages/auth/forgotPassword"));
const SignUp = lazy(() => import("./pages/auth/signUp"));

const Home = lazy(() => import("./pages/main/home/index"));
const OwnerReport = lazy(() => import("./pages/main/report/index"));
const AdminMediaLibrary = lazy(() => import("./pages/main/media_library/index"));
const Refer = lazy(() => import("./pages/main/refer"));
const Profile = lazy(() => import("./pages/main/profile"));
const Addons = lazy(() => import("./pages/main/addons"));
const Stores = lazy(() => import("./pages/main/stores"));
const Users = lazy(() => import("./pages/main/users"));
const TeamMembers = lazy(() => import("./pages/main/team_members/index.js"));
const Roles = lazy(() => import("./pages/main/roles"));
const AuditLogs = lazy(() => import("./pages/main/audit_logs"));
const Plans = lazy(() => import("./pages/main/plans"));
const Automations = lazy(() => import("./pages/main/system/automations"));
const Defaults = lazy(() => import("./pages/main/system/defaults"));
const AISettings    = lazy(() => import("./pages/main/system/aiSettings"));
const AIUsageReport = lazy(() => import("./pages/main/system/aiUsageReport"));
const Apps = lazy(() => import("./pages/main/system/apps"));
const CreditPricing = lazy(() => import("./pages/main/system/creditPricing"));
const EmailTemplates = lazy(() => import("./pages/main/emails/templates"));
const DefaultTemplates = lazy(() => import("./pages/main/emails/templates/defaultTemplates"));
const NewsletterEditor = lazy(() => import("./pages/main/emails/templates/NewsletterEditor"));
const StoreHome = lazy(() => import("./pages/store/home"));
const StoreNewMenu = lazy(() => import("./pages/store/newmenu"));
const StoreMealDeal = lazy(() =>
  import("./pages/store/menu/mealDeal/mealDeal.js")
);
const EposMenu = lazy(() => import("./pages/store/epos/menu"));
const EposApiKeys = lazy(() => import("./pages/store/epos/apiKeys"));

const Orders = lazy(() => import("./pages/store/orders/Orders"));
const OrdersReviews = lazy(() => import("./pages/store/orders/Reviews"));
const OrdersReservations = lazy(() => import("./pages/store/orders/Reservations"));

const StorePromotions = lazy(() => import("./pages/store/promotions"));

const StoreLoyalty = lazy(() => import("./pages/store/marketing/loyalty"));
const StoreGames = lazy(() => import("./pages/store/marketing/games"));
const StoreWheel = lazy(() => import("./pages/store/marketing/wheel"));
const StoreScratch = lazy(() => import("./pages/store/marketing/scratch"));
const StoreBurger = lazy(() => import("./pages/store/marketing/burger"));
const Campaigns = lazy(() => import("./pages/store/marketing/campaigns"));
const SmsCredits = lazy(() => import("./pages/store/marketing/credits"));
const MarketingAutomation = lazy(() =>
  import("./pages/store/marketing/automation")
);

const GeneralAccount = lazy(() =>
  import("./pages/store/settings/general/account.js")
);
const GeneralUsers = lazy(() =>
  import("./pages/store/settings/general/GeneralUsers")
);
const GeneralPlans = lazy(() => import("./pages/store/settings/general/plans"));
const GeneralPaymentCards = lazy(() =>
  import("./pages/store/settings/general/paymentCards")
);
const Drivers = lazy(() =>
  import("./pages/store/settings/general/Drivers")
);
const LiveTracking = lazy(() =>
  import("./pages/store/orders/LiveTracking")
);
const DriversSettings = lazy(() =>
  import("./pages/store/settings/general/DriversSettings")
);
const DriverReports = lazy(() =>
  import("./pages/store/orders/DriverReports")
);
const GeneralBillingData = lazy(() =>
  import("./pages/store/settings/general/billingData")
);

const OrderingSettings = lazy(() =>
  import("./pages/store/settings/order/Ordering")
);
const ReceivingOrdersSettings = lazy(() =>
  import("./pages/store/settings/order/ReceivingOrders")
);
const OrderFormSettings = lazy(() =>
  import("./pages/store/settings/order/OrderForm")
);
const GdprTosSettings = lazy(() =>
  import("./pages/store/settings/order/GDPR_TOS")
);
const StoreOrderingTime = lazy(() =>
  import("./pages/store/settings/order/orderingTime")
);
const StoreOrderTypes = lazy(() =>
  import("./pages/store/settings/order/orderTypes")
);
const PrintingOrdersSettings = lazy(() =>
  import("./pages/store/settings/order/Printing")
);

const RecommendedProductsSettings = lazy(() =>
  import("./pages/store/settings/order/RecommendedProducts")
);
const ReviewSettings = lazy(() =>
  import("./pages/store/settings/order/ReviewSettings")
);
const StoreWorkingHours = lazy(() =>
  import("./pages/store/settings/store/workingHours")
);
const StoreLocationContact = lazy(() =>
  import("./pages/store/settings/store/locationContact")
);
const StoreDeliveryZones = lazy(() =>
  import("./pages/store/settings/store/deliveryZones")
);
const StoreCollectionZones = lazy(() =>
  import("./pages/store/settings/store/collectionZones")
);
const StorePaymentTypes = lazy(() =>
  import("./pages/store/settings/store/paymentTypes")
);
const StoreBranding = lazy(() =>
  import("./pages/store/settings/store/branding")
);
const AppStores = lazy(() => import("./pages/store/settings/store/appStores"));
const StoreDesign = lazy(() => import("./pages/store/settings/store/design"));
const StoreSettings = lazy(() =>
  import("./pages/store/settings/store/storeSettings.js")
);

const ReportOrderSummary = lazy(() =>
  import("./pages/store/report/orderSummary")
);
const ReportOrderHistory = lazy(() =>
  import("./pages/store/report/orderHistory")
);
const MediaLibraryArchive = lazy(() =>
  import("./pages/store/mediaLibrary")
);
const EmailSettings = lazy(() =>
  import("./pages/store/settings/general/emailSettings")
);
const UberEatsIntegration = lazy(() =>
  import("./pages/store/settings/general/UberEatsIntegration")
);
const ReportCustomers = lazy(() => import("./pages/store/report/customers"));
const ReportBlockedIPs = lazy(() => import("./pages/store/report/blockedIPs"));
const StoreInvoices = lazy(() => import("./pages/store/report/storeInvoices"));
const ReportAnalytics = lazy(() => import("./pages/store/report/analytics"));
const BusinessCopilot = lazy(() => import("./pages/store/copilot"));
const MarketingAgent = lazy(() => import("./pages/store/marketing/agent"));
const GrowthAI = lazy(() => import("./pages/store/growth"));
const PhoneAgent = lazy(() => import("./pages/store/phone_agent"));

const WebsiteDashboardLayout = lazy(() =>
  import("./components/layouts/WebsiteDashboard")
);
const MainLayout = lazy(() => import("./components/layouts/MainDashboard"));

const NoMatchCmp = lazy(() => import("./components/shared/NoMatch"));
const AccessDenied = lazy(() => import("./components/shared/AccessDenied"));
const Onboarding = lazy(() => import("./pages/onboarding/index"));
import PermissionGuard from "./components/shared/PermissionGuard";

import PageLoading from "./components/page_loading";

const NoMatch = () => {
  return <NoMatchCmp />;
};

// The AI pages moved under /ai/* when they were grouped into the "AI Assistants"
// menu. Old links (emails, bookmarks, assistant navigation) still land correctly.
const RedirectToAI = ({ page }) => {
  const { storeId } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/store/admin/${storeId}/ai/${page}${search}`} replace />;
};
function App() {
  const [api, contextHolder] = notification.useNotification();
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("sw.js")
        .then(() => navigator.serviceWorker.ready)
        .then(() => {
          api.success({ message: "Service worker ready", duration: 2 });
        })
        .catch(() => {
          api.error({ message: "Service worker failed", duration: 0 });
        });
    }
  }, []);

  const ProtectedRoute = ({ children }) => {
    const navigate = useNavigate();

    // Check if user is logged in
    const state = store.getState();
    const token = state.auth.token;
    if (!token) {
      const location = useLocation();
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    // Get path 
    const path = useLocation().pathname;
    const isSuperAdmin = state.auth.userRole === 'super_admin';
    const isTeamMember = state.auth.userRole === 'team_member';

    if(!isSuperAdmin && !isTeamMember && state.auth.userData?.meta?.onboarding !== 'completed' && path !== "/onboarding") {
      const location = useLocation();
      return <Navigate to="/onboarding" replace state={{ from: location }} />;
    } 


    return (
      <>
        {contextHolder}
        {children}
      </>
    );
  };

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoading />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/forgot-password/:token" element={<ForgotPassword />} />
          <Route path="/signup" element={<SignUp />} />

          <Route path="/access-denied" element={<AccessDenied />} />

          <Route
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Home />} />
            <Route path="refer" element={<Refer />} />
            <Route path="profile" element={<Profile />} />
            <Route path="addons" element={<Addons />} />
            <Route path="stores" element={<Stores />} />
            <Route path="stores/:userId" element={<Stores />} />
            <Route path="users" element={<Users />} />
            <Route
              path="team_members"
              element={
                <PermissionGuard superAdminOnly>
                  <TeamMembers />
                </PermissionGuard>
              }
            />
            <Route
              path="roles"
              element={
                <PermissionGuard superAdminOnly>
                  <Roles />
                </PermissionGuard>
              }
            />
            <Route
              path="audit-logs"
              element={
                <PermissionGuard superAdminOnly>
                  <AuditLogs />
                </PermissionGuard>
              }
            />
            <Route path="plans" element={<Plans />} />
            <Route
              path="owner-report"
              element={
                <PermissionGuard superAdminOnly>
                  <OwnerReport />
                </PermissionGuard>
              }
            />
            <Route
              path="admin-media-library"
              element={
                <PermissionGuard superAdminOnly>
                  <AdminMediaLibrary />
                </PermissionGuard>
              }
            />
            <Route path="automations" element={<Automations />} />
            <Route path="defaults" element={<Defaults />} />
            <Route path="ai-settings" element={<AISettings />} />
            <Route path="ai-usage" element={<AIUsageReport />} />
            <Route path="apps" element={<Apps />} />
            <Route path="credit-pricing" element={<CreditPricing />} />
            <Route path="emails/templates" element={<EmailTemplates />} />
            <Route path="emails/templates/edit/new" element={<NewsletterEditor />} />
            <Route path="emails/templates/edit/:id" element={<NewsletterEditor />} />
            <Route path="emails/default-templates" element={<DefaultTemplates />} />
          </Route>

          <Route
            path="/store/admin"
            element={
              <ProtectedRoute>
                <WebsiteDashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path=":storeId" element={<StoreHome />} />
            <Route path=":storeId/menu" element={<StoreNewMenu />} />
            <Route path=":storeId/menu/meal_deal" element={<StoreMealDeal />} />
            <Route
              path=":storeId/menu/recommended_products"
              element={<RecommendedProductsSettings />}
            />

            <Route path=":storeId/epos" element={<EposMenu />} />
            <Route path=":storeId/epos/api-keys" element={<EposApiKeys />} />

            <Route path=":storeId/orders" element={<Orders />} />
            <Route path=":storeId/orders/reviews" element={<OrdersReviews />} />
            <Route path=":storeId/orders/reservations" element={<OrdersReservations />} />

            <Route path=":storeId/promotions" element={<StorePromotions />} />

            <Route
              path=":storeId/marketing/loyality_program"
              element={<StoreLoyalty />}
            />
            <Route
              path=":storeId/marketing/games"
              element={<StoreGames />}
            />
            <Route
              path=":storeId/marketing/lucky_wheel"
              element={<StoreWheel />}
            />
            <Route
              path=":storeId/marketing/scratch_card"
              element={<StoreScratch />}
            />
            <Route
              path=":storeId/marketing/burger_stack"
              element={<StoreBurger />}
            />
            <Route
              path=":storeId/marketing/campaigns"
              element={<Campaigns />}
            />
            <Route
              path=":storeId/ai/copilot"
              element={<BusinessCopilot />}
            />
            <Route
              path=":storeId/ai/growth"
              element={<GrowthAI />}
            />
            <Route
              path=":storeId/ai/phone_agent"
              element={<PhoneAgent />}
            />
            <Route
              path=":storeId/ai/marketing_agent"
              element={<MarketingAgent />}
            />
            <Route
              path=":storeId/marketing/agent"
              element={<RedirectToAI page="marketing_agent" />}
            />
            <Route
              path=":storeId/report/growth"
              element={<RedirectToAI page="growth" />}
            />
            <Route
              path=":storeId/marketing/sms_credits"
              element={<SmsCredits />}
            />
            <Route
              path=":storeId/marketing/marketing_automation"
              element={<MarketingAutomation />}
            />

            <Route
              path=":storeId/general/account"
              element={<GeneralAccount />}
            />
            <Route path=":storeId/general/users" element={<GeneralUsers />} />
            <Route path=":storeId/general/plans" element={<GeneralPlans />} />
            <Route
              path=":storeId/general/payment_cards"
              element={<GeneralPaymentCards />}
            />
            <Route
              path=":storeId/drivers"
              element={<Drivers />}
            />
            <Route
              path=":storeId/drivers/settings"
              element={<DriversSettings />}
            />
            <Route
              path=":storeId/live-tracking"
              element={<LiveTracking />}
            />
            <Route
              path=":storeId/driver-reports"
              element={<DriverReports />}
            />
            <Route
              path=":storeId/general/billing_data"
              element={<GeneralBillingData />}
            />
            <Route
              path=":storeId/general/printing"
              element={<PrintingOrdersSettings />}
            />
            <Route
              path=":storeId/general/gdpr_tos"
              element={<GdprTosSettings />}
            />

            <Route
              path=":storeId/order/ordering"
              element={<OrderingSettings />}
            />
            <Route
              path=":storeId/order/receiving"
              element={<ReceivingOrdersSettings />}
            />
            <Route
              path=":storeId/order/order_form"
              element={<OrderFormSettings />}
            />
            <Route
              path=":storeId/order/review_settings"
              element={<ReviewSettings />}
            />
            <Route
              path=":storeId/order/ordering_time"
              element={<StoreOrderingTime />}
            />
            <Route
              path=":storeId/order/order_types"
              element={<StoreOrderTypes />}
            />
            <Route
              path=":storeId/order/payment_types"
              element={<StorePaymentTypes />}
            />

            <Route
              path=":storeId/store/working_hours"
              element={<StoreWorkingHours />}
            />
            <Route
              path=":storeId/store/location_contact"
              element={<StoreLocationContact />}
            />
            <Route
              path=":storeId/store/delivery_zones"
              element={<StoreDeliveryZones />}
            />
            <Route
              path=":storeId/store/collection_zones"
              element={<StoreCollectionZones />}
            />
            <Route path=":storeId/store/branding" element={<StoreBranding />} />
            <Route path=":storeId/store/app_stores" element={<AppStores />} />
            <Route path=":storeId/store/design" element={<StoreDesign />} />
            <Route
              path=":storeId/store/store_content"
              element={<StoreSettings />}
            />

            <Route
              path=":storeId/report/order_summary"
              element={<ReportOrderSummary />}
            />
            <Route
              path=":storeId/report/order_history"
              element={<ReportOrderHistory />}
            />
            <Route
              path=":storeId/general/media_library"
              element={<MediaLibraryArchive />}
            />  
            <Route
              path=":storeId/general/email_settings"
              element={<EmailSettings />}
            />
            <Route
              path=":storeId/general/ubereats"
              element={<UberEatsIntegration />}
            />
            <Route
              path=":storeId/general/email_templates"
              element={<EmailTemplates />}
            />  
            <Route
              path=":storeId/report/customers"
              element={<ReportCustomers />}
            />
            <Route
              path=":storeId/report/blocked_ips"
              element={<ReportBlockedIPs />}
            />
            <Route
              path=":storeId/report/store_invoices"
              element={<StoreInvoices />}
            />
            <Route
              path=":storeId/report/analytics"
              element={<ReportAnalytics />}
            />
            <Route
              path=":storeId/report/copilot"
              element={<RedirectToAI page="copilot" />}
            />
          </Route>
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
            } />
          <Route path="*" element={<NoMatch />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

const root = createRoot(document.getElementById("loopinfoodbell"));

root.render(
  <StyleProvider hashPriority="high" layer>
    <ConfigProvider
      theme={{
        components: {
          Radio: {
            buttonSolidCheckedBg: "#f59e09",
            buttonSolidCheckedActiveBg: "#f59e09",
            buttonSolidCheckedHoverBg: "#f59e09",
          },
          Slider: {
            dotSize: 10,
            handleSize: 14,
            handleSizeHover: 16,
            railSize: 8,
          },
          Select: {
            zIndexPopup: "9999999",
          },
        },
      }}
    >
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <App />
        </PersistGate>
      </Provider>
    </ConfigProvider>
  </StyleProvider>
);
